import Button from '@/app/components/common/Button'
import { DisplayData } from '@/app/components/LocationDataHandler'
import { Icons } from '@/app/components/ui/Icons'
import {
	AvailableInputTypes,
	DATE_FORMAT_QUERY,
	DEFAULT_CHECK_IN_DATE,
	DEFAULT_CHECK_OUT_DATE,
	DEFAULT_NUMBER_OF_GUESTS,
	HOTEL_DEFAULT_CHECK_IN_WEEKS,
	HOTEL_DEFAULT_STAY_DAYS,
	INPUT,
	LG_BREAKPOINT,
	ROOM_GUESTS_SEPARATOR,
} from '@/app/constants'
import eventBus from '@/app/eventBus'
import { emitSmallBookingClickGAEvent } from '@/app/ga-event-helpers'
import { PS_EVENTS } from '@/app/ps-events'
import {
	Allocation,
	SanityData,
	SearchItem,
	SearchPlaceType,
} from '@/app/types'
import {
	determineSearchPlaceFromPath,
	getLocaleByCode,
	isCountry,
	isLowercaseMonth,
	shouldUseCompactBookingDefaults,
	smoothScrollTo,
} from '@/app/utils'
import { parseStayDateFromUrlOrLegacy, requireStayDateFromIso } from '@/app/utils/calendar-date'
import { useMediaQuery } from '@uidotdev/usehooks'
import classNames from 'classnames'
import { addDays, addWeeks, format, isValid } from 'date-fns'
import { startCase } from 'lodash'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useParams, useSearchParams } from 'next/navigation'
import { FC, useEffect, useRef, useState } from 'react'

interface CompactBookingProps {
	displayValue: DisplayData | null
	defaultLocation: SearchItem | null
	isMainPage: boolean
	sanityData?: SanityData
	loading?: boolean
	customHeading?: boolean
	isHotelPage?: boolean
	isExpanded?: boolean
	dest?: string[]
	onToggleExpand: (expand: boolean) => void
}

const SHORT_DATE_FORMAT = 'MMM dd'
const b = 'compact-booking'
const CompactBooking: FC<CompactBookingProps> = (props) => {
	const { onToggleExpand, isExpanded, dest, displayValue } = props
	const { lang } = useParams()
	const locale = getLocaleByCode(lang as string)
	const t = useTranslations('Form')
	const isDesktop = useMediaQuery(LG_BREAKPOINT)
	const compactCalendarInputRef = useRef<HTMLDivElement>(null)
	const calendarOpenedRef = useRef(false)
	const isHotelResultsPage =
		determineSearchPlaceFromPath(dest as string[], false).type ===
		SearchPlaceType.HOTEL

	const searchParams = useSearchParams()
	const checkInDateParam = searchParams.get('check-in-date')
	const checkOutDateParam = searchParams.get('check-out-date')
	const [checkInDate, setCheckInDate] = useState<string>('')
	const [checkOutDate, setCheckOutDate] = useState<string>('')
	const [stayCheckInIso, setStayCheckInIso] = useState<string>('')
	const [stayCheckOutIso, setStayCheckOutIso] = useState<string>('')

	const getGuestsCount = (): number => {
		const roomParams: string[] = searchParams.getAll('room')

		if (roomParams.length === 0) {
			return DEFAULT_NUMBER_OF_GUESTS
		}

		const rooms: Allocation[] = roomParams.map((room: string): Allocation => {
			const [adults, children = ''] = room.split(ROOM_GUESTS_SEPARATOR)
			const childAges = children.split(',').filter(Boolean).map(Number)

			return {
				adults: Math.max(parseInt(adults, 10) || 0, 0),
				childAge: childAges.filter((age) => !isNaN(age)),
			}
		})

		return rooms.reduce(
			(total, room) => total + room.adults + room.childAge.length,
			0
		)
	}

	useEffect(() => {
		let newCheckInDate = ''
		let newCheckOutDate = ''
		let inIso = ''
		let outIso = ''

		if (shouldUseCompactBookingDefaults(checkInDateParam, checkOutDateParam)) {
			if (isHotelResultsPage) {
				const checkIn = addWeeks(new Date(), HOTEL_DEFAULT_CHECK_IN_WEEKS)
				const checkOut = addDays(checkIn, HOTEL_DEFAULT_STAY_DAYS)
				newCheckInDate = format(checkIn, SHORT_DATE_FORMAT, { locale })
				newCheckOutDate = format(checkOut, SHORT_DATE_FORMAT, { locale })
				inIso = format(checkIn, DATE_FORMAT_QUERY)
				outIso = format(checkOut, DATE_FORMAT_QUERY)
			} else {
				const defaultIn = requireStayDateFromIso(
					DEFAULT_CHECK_IN_DATE,
					'CompactBooking default check-in'
				)
				const defaultOut = requireStayDateFromIso(
					DEFAULT_CHECK_OUT_DATE,
					'CompactBooking default check-out'
				)
				newCheckInDate = format(defaultIn, SHORT_DATE_FORMAT, { locale })
				newCheckOutDate = format(defaultOut, SHORT_DATE_FORMAT, { locale })
				inIso = DEFAULT_CHECK_IN_DATE
				outIso = DEFAULT_CHECK_OUT_DATE
			}
		} else {
			const dIn = parseStayDateFromUrlOrLegacy(checkInDateParam as string)
			const dOut = parseStayDateFromUrlOrLegacy(checkOutDateParam as string)
			newCheckInDate = isValid(dIn)
				? format(dIn, SHORT_DATE_FORMAT, { locale })
				: ''
			newCheckOutDate = isValid(dOut)
				? format(dOut, SHORT_DATE_FORMAT, { locale })
				: ''
			inIso = isValid(dIn) ? format(dIn, DATE_FORMAT_QUERY) : ''
			outIso = isValid(dOut) ? format(dOut, DATE_FORMAT_QUERY) : ''
		}

		setCheckInDate(newCheckInDate)
		setCheckOutDate(newCheckOutDate)
		setStayCheckInIso(inIso)
		setStayCheckOutIso(outIso)
	}, [isHotelResultsPage, checkInDateParam, checkOutDateParam, locale])

	const handleInputClick = (inputType: AvailableInputTypes) => {
		emitSmallBookingClickGAEvent()

		onToggleExpand(true)

		if (inputType === INPUT.CALENDAR_FROM || inputType === INPUT.CALENDAR_TO) {
			calendarOpenedRef.current = true
		}

		setTimeout(() => {
			eventBus.publish(PS_EVENTS.INPUT_FOCUS, inputType)
		}, 200)
	}

	const handleClose = () => {
		onToggleExpand(false)
	}

	const handleReload = () => {
		const backTabPartner = searchParams.get('b');
		const frontTabPartner = searchParams.get('f');
		if ((backTabPartner || frontTabPartner) && props.isHotelPage) {
			eventBus.publish(PS_EVENTS.HANDLE_SEARCH)
			return;
		}
		smoothScrollTo(0, 0)

		if (typeof window !== 'undefined') {
			setTimeout(() => window.location.reload()), 200
		}
	}

	useEffect(() => {
		const unsubscribe = eventBus.subscribe(
			PS_EVENTS.INPUT_FOCUS,
			(inputType: AvailableInputTypes) => {
				if (!isDesktop && inputType === INPUT.MAIN_FORM) {
					setTimeout(() => onToggleExpand(true), 0)
				}

				if (
					inputType === INPUT.CALENDAR_FROM &&
					compactCalendarInputRef.current &&
					!isExpanded &&
					!calendarOpenedRef.current
				) {
					calendarOpenedRef.current = true
					setTimeout(() => compactCalendarInputRef.current?.click(), 0)
				}
			}
		)

		return () => {
			unsubscribe()
		}
	}, [])

	const getInputValue = () => {
		const readableLocationValue =
			displayValue?.displayValue || displayValue?.displayName || ''
		const normalizedCountryValue = startCase(readableLocationValue)
		return isCountry(dest as string[])
			? normalizedCountryValue
			: readableLocationValue
	}

	const capitalize = !isLowercaseMonth(lang as string);

	return (
		<div
			className={classNames(b, {
				[`${b}--expanded`]: isExpanded,
				'max-lg:border-none w-0 h-0 overflow-hidden !p-0': isExpanded,
			})}
		>
			<div
				className={classNames(`${b}__trigger`, {
					[`${b}__trigger--hidden`]: isExpanded,
				})}
				data-test-id="compact-booking-trigger"
			>
				{/* Destkop fields */}
				<div className={classNames(`${b}__fields`)}>
					<div
						className={`${b}__field w-[214px] pe-0`}
						onClick={() => handleInputClick(INPUT.LOCATION)}
					>
						<span className="w-full truncate">
							{getInputValue() || t('searchForHotels')}
						</span>
					</div>

					<div className={`${b}__divider`} />

					<div
						className={classNames(`${b}__field w-[165px]`, {
							capitalize
						})}
						onClick={() => handleInputClick(INPUT.CALENDAR_FROM)}
						ref={compactCalendarInputRef}
						data-test-id="compact-booking-calendar-field"
						data-stay-check-in={stayCheckInIso}
						data-stay-check-out={stayCheckOutIso}
					>
						{checkInDate} - {checkOutDate}
					</div>

					<div className={`${b}__divider`} />

					<div
						className={`${b}__field w-[130px] pe-0`}
						onClick={() => handleInputClick(INPUT.GUESTS_SELECT)}
					>
						{t('guestsCount', { count: getGuestsCount() })}
					</div>

					<Button
						id="reload-page-button"
						onClick={handleReload}
						name="Reload"
						className={`${b}__search-button `}
					>
						<Icons.iconLens color='white' />
					</Button>
				</div>
				{/* Mobile field */}
				<div
					className={`${b}__field ${b}__field--mobile`}
					onClick={() => onToggleExpand(true)}
				>
					<span className="font-semibold w-full truncate">
						{props.displayValue?.displayValue ||
							props.displayValue?.displayName ||
							t('searchForHotels')}
					</span>
					<span className="text-xs text-text-700 flex items-center">
						{checkInDate} - {checkOutDate}{' '}
						<span className="w-0.5 h-0.5 bg-text-400 rounded-full flex mx-1" />{' '}
						{t('guestsCount', { count: getGuestsCount() })}
					</span>
				</div>
			</div>
		</div>
	)
}

export default dynamic(() => Promise.resolve(CompactBooking), {
	ssr: false,
	loading: () => <div className="min-h-14 w-full" />
})
